import { useMemo } from 'react';
import { useRequiredFields } from '@/features/system/hooks/useRequiredFields';
import type { RequiredField } from '@/features/system/types/requiredFields.types';

/**
 * Hook to manage required fields configuration for providers.
 * Uses the existing useRequiredFields hook but specifically for the 'company' category.
 *
 * @returns {
 *   isFieldRequired: (fieldKey: string) => boolean - Helper to check if a field is required
 *   getRequiredFieldKeys: () => string[] - Helper to get all required field keys
 * }
 */
export const useProviderRequiredFields = () => {
  const { config } = useRequiredFields('company');

  const normalizeCompanyFieldKey = (fieldKey: string): string => {
    if (fieldKey === 'pt_priority' || fieldKey === 'preference') {
      return 'preference.id';
    }

    return fieldKey;
  };

  const isRoROptionalCompanyField = (fieldKey: string): boolean =>
    fieldKey === 'transportation_subcontractor'
    || fieldKey === 'exempted_from_location_codes';

  /**
   * Helper function to check if a specific field is required
   * Checks both mandatory fields and optional fields marked as required
   *
   * @param fieldKey - The backend field key to check (e.g., 'name', 'contact_email')
   * @returns true if the field is required, false otherwise
   */
  const isFieldRequired = useMemo(() => {
    return (fieldKey: string): boolean => {
      if (!config) return false;
      const normalizedFieldKey = normalizeCompanyFieldKey(fieldKey);

      if (isRoROptionalCompanyField(normalizedFieldKey)) {
        return false;
      }

      // Check if it's a mandatory field
      const isMandatory = config.mandatoryFields.some(
        (field: RequiredField) => normalizeCompanyFieldKey(field.fieldKey) === normalizedFieldKey && field.isRequired
      );

      if (isMandatory) return true;

      // Check if it's an optional field marked as required
      const isOptionalRequired = config.optionalFields.some(
        (field: RequiredField) => normalizeCompanyFieldKey(field.fieldKey) === normalizedFieldKey && field.isRequired
      );

      return isOptionalRequired;
    };
  }, [config]);

  /**
   * Helper function to get all required field keys
   * Useful for validation logic
   *
   * @returns Array of required field keys
   */
  const getRequiredFieldKeys = useMemo(() => {
    return (): string[] => {
      if (!config) return [];

      const mandatoryKeys = config.mandatoryFields
        .filter((field: RequiredField) => field.isRequired)
        .map((field: RequiredField) => normalizeCompanyFieldKey(field.fieldKey));

      const optionalRequiredKeys = config.optionalFields
        .filter((field: RequiredField) => field.isRequired)
        .map((field: RequiredField) => normalizeCompanyFieldKey(field.fieldKey));

      return [...new Set([...mandatoryKeys, ...optionalRequiredKeys])]
        .filter((fieldKey: string) => !isRoROptionalCompanyField(fieldKey));
    };
  }, [config]);

  return {
    isFieldRequired,
    getRequiredFieldKeys,
  };
};
